import React, { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../../../../../providers/AuthContext";
import { useNavigate } from "react-router-dom";
import { getDataWidget, getWidgetById } from "../../../../../services/apiWidgests";
import Spinner from "../../../../../components/Spinner";
import { useDashboards } from "../../../../../providers/DashboardContext";
import DatabaseOffIco from "../../../../../components/icons/DatabaseOffIco";
import ArrowUpwardIco from "../../../../../components/icons/ArrowUpwardIco";
import ArrowDownwardIco from "../../../../../components/icons/ArrowDownwardIco";

const PlanningTable = ({ id }) => {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const { selectedDatarange } = useDashboards();

  const [config, setConfig] = useState({});
  const [fetchingWidgetInfo, setFetchingWidgetInfo] = useState(true);
  const [fetchingWidgetData, setFetchingWidgetData] = useState(false);
  const [rows, setRows] = useState([]);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // 🔥 excel filter state
  const [columnFilters, setColumnFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");

  const filterRef = useRef(null);

  const nDecimals = config?.nDecimals ?? 2;
  const headers = config?.headers || [];

  // =====================================================
  // 🎨 header color dinámico
  // =====================================================
  const headerBgColor = useMemo(() => {
    const palette = config?.colorPalette;
    const fixed = config?.fixedColor;

    if (palette === "fixed" && fixed) return fixed;
    if (palette === "red") return "#dc2626";
    if (palette === "blue") return "#2563eb";
    if (palette === "pastel") return "#7dd3fc";

    return "#f3f4f6";
  }, [config]);

  const headerTextColor = config?.textColor || "#374151";

  // =====================================================
  // 📅 días dinámicos
  // =====================================================
  const getDayKeys = (data) => {
    const set = new Set();
    data.forEach((r) => {
      if (!r?.day) return;
      Object.keys(r.day).forEach((d) => set.add(d));
    });
    return Array.from(set).sort((a, b) => new Date(a) - new Date(b));
  };

  const dayColumns = useMemo(() => getDayKeys(rows), [rows]);

  const formatDayHeader = (dateStr) => {
    try {
      const [y, m, d] = dateStr.split("-").map(Number);
      const localDate = new Date(y, m - 1, d);

      return localDate.toLocaleDateString("es-ES", {
        weekday: "short",
        day: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const formatPercent = (v) =>
    `${Number(v ?? 0).toFixed(nDecimals)}%`;

  // =====================================================
  // fetch
  // =====================================================
  const refreshWidgetInfo = async () => {
    try {
      setFetchingWidgetInfo(true);
      const response = await getWidgetById(id, auth?.token);
      setConfig(response?.configuration || {});
    } catch {
      navigate("/logout");
    } finally {
      setFetchingWidgetInfo(false);
    }
  };

  const refreshWidgetData = async () => {
    try {
      setFetchingWidgetData(true);
      const response = await getDataWidget(id, {}, auth?.token);
      setRows(response || []);
    } catch {
      navigate("/logout");
    } finally {
      setFetchingWidgetData(false);
    }
  };

  useEffect(() => {
    refreshWidgetInfo();
  }, [id]);

  useEffect(() => {
    refreshWidgetData();
  }, [selectedDatarange, id]);

  // =====================================================
  // 🔽 valores únicos por columna
  // =====================================================
  const getUniqueValues = (key) => {
    const set = new Set();
    rows.forEach((r) => set.add(String(r[key] ?? "--")));
    return Array.from(set).sort();
  };

  // =====================================================
  // 🔽 toggle checkbox
  // =====================================================
  const toggleValue = (key, value) => {
    setColumnFilters((prev) => {
      const current = new Set(prev[key] || []);
      current.has(value) ? current.delete(value) : current.add(value);
      return { ...prev, [key]: Array.from(current) };
    });
  };

  // =====================================================
  // sort
  // =====================================================
  const handleSortHeader = (headerKey) => {
    setSortConfig((prev) => {
      if (prev.key === headerKey) {
        return {
          key: headerKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: headerKey, direction: "asc" };
    });
  };

  // =====================================================
  // 🧠 DATA PROCESADA
  // =====================================================
  const processedRows = useMemo(() => {
    let filtered = [...rows];

    filtered = filtered.filter((row) =>
      Object.entries(columnFilters).every(([key, values]) => {
        if (!values?.length) return true;
        return values.includes(String(row[key] ?? "--"));
      })
    );

    if (!sortConfig.key) return filtered;

    return filtered.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc"
          ? aVal - bVal
          : bVal - aVal;
      }

      return sortConfig.direction === "asc"
        ? String(aVal ?? "").localeCompare(String(bVal ?? ""))
        : String(bVal ?? "").localeCompare(String(aVal ?? ""));
    });
  }, [rows, columnFilters, sortConfig]);

  // =====================================================
  // cerrar dropdown al hacer click afuera
  // =====================================================
  useEffect(() => {
    const handleClick = (e) => {
      if (!filterRef.current?.contains(e.target)) {
        setOpenFilter(null);
        setSearchFilter("");
      }
    };
    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, []);

  // =====================================================
  // empty
  // =====================================================
  const renderEmpty = () => (
    <div className="w-full h-full flex flex-col items-center justify-center -mt-8">
      <span className="text-lg text-gray-700">No hay datos por el momento</span>
      <DatabaseOffIco width="40px" height="40px" fill="gray" />
    </div>
  );

  // =====================================================
  // TABLE
  // =====================================================
  const renderTable = () => (
    <div className="w-full h-full overflow-auto">
      <table className="min-w-full text-sm border border-gray-300">
        <thead
          className="sticky top-0 shadow-sm"
          style={{ backgroundColor: headerBgColor, color: headerTextColor }}
        >
          <tr>
            {headers.map((h) => {
              const keyName = h.displayName || h.alias || h.key;
              const isSorted = sortConfig.key === keyName;
              const uniqueValues = getUniqueValues(keyName);

              const filteredValues = uniqueValues.filter((v) =>
                v.toLowerCase().includes(searchFilter.toLowerCase())
              );

              return (
                <th
                  key={h.key}
                  className="relative px-3 py-2 border text-center text-xs font-semibold"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div
                      className="flex items-center gap-1 cursor-pointer"
                      onClick={() => handleSortHeader(keyName)}
                    >
                      <span>{keyName}</span>

                      {isSorted &&
                        (sortConfig.direction === "asc" ? (
                          <ArrowUpwardIco width="14px" height="14px" fill={headerTextColor} />
                        ) : (
                          <ArrowDownwardIco width="14px" height="14px" fill={headerTextColor} />
                        ))}
                    </div>

                    <button
                      className="text-[10px] px-1 border rounded bg-white text-black flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenFilter(openFilter === keyName ? null : keyName);
                        setSearchFilter("");
                      }}
                    >
                      ▼
                    </button>
                  </div>

                  {openFilter === keyName && (
                    <div
                      ref={filterRef}
                      className="absolute z-50 mt-1 w-48 bg-white border rounded shadow-lg p-2 text-left"
                    >
                      <input
                        className="w-full mb-2 px-1 py-1 text-xs border rounded"
                        placeholder="Buscar..."
                        value={searchFilter}
                        onChange={(e) => setSearchFilter(e.target.value)}
                      />

                      <div className="max-h-40 overflow-auto text-xs">
                        {filteredValues.map((val) => (
                          <label key={val} className="flex gap-2 items-center">
                            <input
                              type="checkbox"
                              checked={columnFilters[keyName]?.includes(val) || false}
                              onChange={() => toggleValue(keyName, val)}
                            />
                            {val}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </th>
              );
            })}

            {dayColumns.map((d) => (
              <th
                key={d}
                className="px-3 py-2  border-gray-400 text-center text-xs font-semibold bg-gray-50 text-gray-600"
              >
                {formatDayHeader(d)}
              </th>
            ))}

            <th className="px-3 py-2  border-gray-400 text-center text-xs">
              Total
            </th>

            <th className="px-3 py-2 border text-center text-xs">Planificado</th>
            <th className="px-3 py-2 border text-center text-xs">Falta</th>
            <th className="px-3 py-2 border text-center text-xs">Avance</th>
            <th className="px-3 py-2 border text-center text-xs">Estado</th>
          </tr>
        </thead>

        <tbody>
          {processedRows.map((row, idx) => (
            <tr key={idx} className="hover:bg-graysens">
              {headers.map((h) => (
                <td key={h.key} className="px-3 py-2 border text-center">
                  {row[h.displayName || h.alias || h.key] ?? "--"}
                </td>
              ))}

              {dayColumns.map((d) => (
                <td key={d} className="px-3 py-2 border-2 border-gray-300 text-right">
                  {Number(row.day?.[d] ?? 0).toFixed(nDecimals)}
                </td>
              ))}

              <td className="px-3 py-2 border-2 border-gray-300 text-right font-semibold">
                {Number(row.executed ?? 0).toFixed(nDecimals)}
              </td>

              <td className="px-3 py-2 border text-right font-semibold">
                {Number(row.goal ?? 0).toFixed(nDecimals)}
              </td>

              <td className="px-3 py-2 border text-right">
                {Number(row.remain ?? 0).toFixed(nDecimals)}
              </td>

              <td className="px-3 py-2 border text-right">
                <span
                  className={`font-semibold ${
                    row.progress >= 100
                      ? "text-green-600"
                      : row.progress >= 70
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}
                >
                  {formatPercent(row.progress)}
                </span>
              </td>

              <td className="px-3 py-2 border text-center">
                <span
                  className={`px-2 py-1 rounded text-xs font-bold ${
                    row.finished
                      ? "bg-green-100 text-green-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {row.finished ? "FINALIZADO" : "EN CURSO"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // =====================================================
  return (
    <div className="w-full h-full p-3 bg-white border border-gray-300 rounded">
      {fetchingWidgetData || fetchingWidgetInfo ? (
        <div className="w-full h-full flex justify-center items-center">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        renderEmpty()
      ) : (
        renderTable()
      )}
    </div>
  );
};

export default PlanningTable;